/**
 * Plan 13-02 — typed wrappers around the 8 `/api/logsheets/checker` endpoints
 * defined in Plan 13-01.
 *
 * Mirrors the Phase 12 `logsheets-maker.ts` service module pattern:
 *  - Uses the singleton `api` axios instance (with JWT interceptor + Token-Expired handler).
 *  - Each function throws an `ApiError` (via `toApiError`) on non-2xx.
 *  - Query params passed via `params:` not URLSearchParams (axios serialises them).
 *  - Multipart upload uses FormData with the Content-Type header cleared so axios
 *    sets the correct `multipart/form-data; boundary=...` automatically.
 */

import { api } from '@/lib/api/axios';
import { toApiError } from '@/lib/api/error';
import type {
  ApproveRequest,
  BulkApproveRequest,
  BulkApproveResult,
  CheckerDetail,
  HistoryItem,
  HistoryListQuery,
  PendingItem,
  PendingListQuery,
  RecentEntry,
  RejectRequest,
} from '@/types/logsheet-checker';
import type { LogsheetDocument } from '@/types/logsheets-maker';
import type { PaginatedResult } from '@/types/roles';

/**
 * GET /api/logsheets/checker/pending — paginated queue of Submitted entries.
 * Permission: logsheet.checker.review
 */
export async function getPendingRequest(
  query: PendingListQuery = {},
): Promise<PaginatedResult<PendingItem>> {
  try {
    const { data } = await api.get<PaginatedResult<PendingItem>>('/api/logsheets/checker/pending', {
      params: {
        ...(query.siteId !== undefined ? { siteId: query.siteId } : {}),
        ...(query.machineId !== undefined ? { machineId: query.machineId } : {}),
        ...(query.fromDate !== undefined && query.fromDate !== ''
          ? { fromDate: query.fromDate }
          : {}),
        ...(query.toDate !== undefined && query.toDate !== '' ? { toDate: query.toDate } : {}),
        ...(query.status !== undefined ? { status: query.status } : {}),
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 20,
      },
    });
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * GET /api/logsheets/checker/pending/:id — full detail for a single Submitted entry.
 * Permission: logsheet.checker.review
 */
export async function getPendingDetailRequest(id: number): Promise<CheckerDetail> {
  try {
    const { data } = await api.get<CheckerDetail>(`/api/logsheets/checker/pending/${String(id)}`);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * GET /api/logsheets/checker/pending/:id/recent-entries — last-5 comparison entries.
 * Permission: logsheet.checker.review
 */
export async function getRecentEntriesRequest(id: number): Promise<readonly RecentEntry[]> {
  try {
    const { data } = await api.get<readonly RecentEntry[]>(
      `/api/logsheets/checker/pending/${String(id)}/recent-entries`,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * POST /api/logsheets/checker/pending/:id/approve — set entry status to Checked.
 * Permission: logsheet.checker.approve
 */
export async function approveRequest(id: number, body: ApproveRequest): Promise<CheckerDetail> {
  try {
    const { data } = await api.post<CheckerDetail>(
      `/api/logsheets/checker/pending/${String(id)}/approve`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * POST /api/logsheets/checker/pending/:id/reject — reject entry with reason + remarks.
 * Permission: logsheet.checker.reject
 */
export async function rejectRequest(id: number, body: RejectRequest): Promise<CheckerDetail> {
  try {
    const { data } = await api.post<CheckerDetail>(
      `/api/logsheets/checker/pending/${String(id)}/reject`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * POST /api/logsheets/checker/pending/:id/document — upload or replace a document.
 * Permission: logsheet.checker.upload-document
 *
 * Multipart upload: Content-Type header cleared so axios derives the correct
 * `multipart/form-data; boundary=...` from FormData.
 */
export async function uploadCheckerDocumentRequest(
  entryId: number,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<LogsheetDocument> {
  try {
    const form = new FormData();
    form.append('file', file);
    const r = await api.request<LogsheetDocument>({
      url: `/api/logsheets/checker/pending/${String(entryId)}/document`,
      method: 'post',
      data: form,
      transformRequest: (d, headers) => {
        (headers as { delete?: (k: string) => unknown }).delete?.('Content-Type');
        delete (headers as Record<string, unknown>)['Content-Type'];
        delete (headers as Record<string, unknown>)['content-type'];
        return d as unknown;
      },
      onUploadProgress: (evt) => {
        if (onProgress !== undefined && typeof evt.total === 'number' && evt.total > 0) {
          onProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      },
    });
    return r.data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * GET /api/logsheets/checker/history — checker's own action history.
 * Permission: logsheet.checker.read-history
 */
export async function getCheckerHistoryRequest(
  query: HistoryListQuery = {},
): Promise<PaginatedResult<HistoryItem>> {
  try {
    const { data } = await api.get<PaginatedResult<HistoryItem>>('/api/logsheets/checker/history', {
      params: {
        ...(query.status !== undefined ? { status: query.status } : {}),
        ...(query.siteId !== undefined ? { siteId: query.siteId } : {}),
        ...(query.fromDate !== undefined && query.fromDate !== ''
          ? { fromDate: query.fromDate }
          : {}),
        ...(query.toDate !== undefined && query.toDate !== '' ? { toDate: query.toDate } : {}),
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 20,
      },
    });
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * POST /api/logsheets/checker/pending/bulk-approve — approve up to 100 entries.
 * Permission: logsheet.checker.bulk-approve
 */
export async function bulkApproveRequest(body: BulkApproveRequest): Promise<BulkApproveResult> {
  try {
    const { data } = await api.post<BulkApproveResult>(
      '/api/logsheets/checker/pending/bulk-approve',
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}
